import React, { useReducer, useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane, 
  faComment, 
  faRobot, 
  faDatabase, 
  faCheck, 
  faRefresh, 
  faThumbsUp,
  faQuestion,
  faMicrophone,
  faMicrophoneSlash,
  faVolumeUp,
  faStop,
  faChevronDown,
  faChevronUp,
  faBook,
  faBrain,
  faGlobe,
  faTools
} from '@fortawesome/free-solid-svg-icons';
import { chatAPI } from '../../services/api';
import { Skeleton } from '../Skeleton';
import { OpinionModal } from '../shared/OpinionModal';
import './ChatComparison.css';

// TypeScript declarations for Web Speech API
interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onstart: ((this: SpeechRecognition, ev: Event) => any) | null;
  onend: ((this: SpeechRecognition, ev: Event) => any) | null;
  onerror: ((this: SpeechRecognition, ev: SpeechRecognitionErrorEvent) => any) | null;
  onresult: ((this: SpeechRecognition, ev: SpeechRecognitionEvent) => any) | null;
}

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
  message?: string;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
declare var SpeechRecognition: {
  prototype: SpeechRecognition;
  new(): SpeechRecognition;
};

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface Answer {
  type: 'rag' | 'basic';
  content: string;
  has_sources: boolean;
  // GPT-OSS advanced features
  thinking_process?: string | null;
  web_search_queries?: string[] | null;
  web_search_results?: string[] | null;
  tool_calls?: string[] | null;
  confidence_level?: string | null;
}

interface ChatResponse {
  conversation_id: number;
  answers: Answer[];
}

interface ConversationHistory {
  id: number;
  question: string;
  rag_answer: string;
  basic_answer: string;
  selected_answer?: string;
  created_at: string;
  feedback?: string;
}

interface UserProfile {
  id: number;
  email: string;
  full_name: string;
  clinical_info?: string;
}

// State management with useReducer for better state transitions
const initialState = {
  question: '',
  response: null as ChatResponse | null,
  selectedAnswer: null as string | null,
  loading: false,
  error: '',
  showFeedbackPopup: false,
  conversationHistory: [] as ConversationHistory[],
  userProfile: null as UserProfile | null,
  progress: 0,
  progressStage: 'preparing' as 'preparing' | 'sending' | 'analyzing' | 'generating' | 'finalizing' | 'complete',
  followUpQuestion: '',
  showManual: localStorage.getItem('radonek-show-manual') ? JSON.parse(localStorage.getItem('radonek-show-manual')!) : false,
  isListening: false,
  isSpeaking: false,
  speechSupported: false,
  // Streaming states
  isStreaming: false,
  ragStreamContent: '',
  basicStreamContent: '',
  currentStage: '',
  useStreaming: localStorage.getItem('radonek-use-streaming') ? JSON.parse(localStorage.getItem('radonek-use-streaming')!) : true,
  // Advanced features expansion state
  showRagAdvancedFeatures: false,
  showBasicAdvancedFeatures: false,
  // Long wait detection for senior-friendly UX
  waitStartTime: null as number | null,
  isLongWait: false,
};

type Action =
  | { type: 'SET_QUESTION'; payload: string }
  | { type: 'SET_RESPONSE'; payload: ChatResponse | null }
  | { type: 'SET_SELECTED_ANSWER'; payload: string | null }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'SET_SHOW_FEEDBACK_POPUP'; payload: boolean }
  | { type: 'SET_CONVERSATION_HISTORY'; payload: ConversationHistory[] }
  | { type: 'SET_USER_PROFILE'; payload: UserProfile | null }
  | { type: 'SET_PROGRESS'; payload: number }
  | { type: 'SET_PROGRESS_STAGE'; payload: typeof initialState.progressStage }
  | { type: 'SET_FOLLOW_UP_QUESTION'; payload: string }
  | { type: 'SET_SHOW_MANUAL'; payload: boolean }
  | { type: 'SET_IS_LISTENING'; payload: boolean }
  | { type: 'SET_IS_SPEAKING'; payload: boolean }
  | { type: 'SET_SPEECH_SUPPORTED'; payload: boolean }
  | { type: 'SET_IS_STREAMING'; payload: boolean }
  | { type: 'SET_RAG_STREAM_CONTENT'; payload: string }
  | { type: 'SET_BASIC_STREAM_CONTENT'; payload: string }
  | { type: 'APPEND_RAG_CHUNK'; payload: string }
  | { type: 'APPEND_BASIC_CHUNK'; payload: string }
  | { type: 'SET_CURRENT_STAGE'; payload: string }
  | { type: 'SET_USE_STREAMING'; payload: boolean }
  | { type: 'TOGGLE_RAG_ADVANCED_FEATURES' }
  | { type: 'TOGGLE_BASIC_ADVANCED_FEATURES' }
  | { type: 'SUBMIT_QUESTION' }
  | { type: 'SUBMIT_FOLLOW_UP' }
  | { type: 'RESET_CHAT' }
  | { type: 'RESET_STREAMING' }
  | { type: 'SET_WAIT_START_TIME'; payload: number | null }
  | { type: 'SET_IS_LONG_WAIT'; payload: boolean };


const chatReducer = (state: typeof initialState, action: Action): typeof initialState => {
  switch (action.type) {
    case 'SET_QUESTION': return { ...state, question: action.payload };
    case 'SET_RESPONSE': return { ...state, response: action.payload };
    case 'SET_SELECTED_ANSWER': return { ...state, selectedAnswer: action.payload };
    case 'SET_LOADING': return { ...state, loading: action.payload };
    case 'SET_ERROR': return { ...state, error: action.payload, loading: false, isStreaming: false };
    case 'SET_SHOW_FEEDBACK_POPUP': return { ...state, showFeedbackPopup: action.payload };
    case 'SET_CONVERSATION_HISTORY': return { ...state, conversationHistory: action.payload };
    case 'SET_USER_PROFILE': return { ...state, userProfile: action.payload };
    case 'SET_PROGRESS': return { ...state, progress: action.payload };
    case 'SET_PROGRESS_STAGE': return { ...state, progressStage: action.payload };
    case 'SET_FOLLOW_UP_QUESTION': return { ...state, followUpQuestion: action.payload };
    case 'SET_SHOW_MANUAL': 
      localStorage.setItem('radonek-show-manual', JSON.stringify(action.payload));
      return { ...state, showManual: action.payload };
    case 'SET_IS_LISTENING': return { ...state, isListening: action.payload };
    case 'SET_IS_SPEAKING': return { ...state, isSpeaking: action.payload };
    case 'SET_SPEECH_SUPPORTED': return { ...state, speechSupported: action.payload };
    case 'SET_IS_STREAMING': return { ...state, isStreaming: action.payload };
    case 'SET_RAG_STREAM_CONTENT': return { ...state, ragStreamContent: action.payload };
    case 'SET_BASIC_STREAM_CONTENT': return { ...state, basicStreamContent: action.payload };
    case 'APPEND_RAG_CHUNK': return { ...state, ragStreamContent: state.ragStreamContent + action.payload };
    case 'APPEND_BASIC_CHUNK': return { ...state, basicStreamContent: state.basicStreamContent + action.payload };
    case 'SET_CURRENT_STAGE': return { ...state, currentStage: action.payload };
    case 'SET_USE_STREAMING': 
      localStorage.setItem('radonek-use-streaming', JSON.stringify(action.payload));
      return { ...state, useStreaming: action.payload };
    case 'TOGGLE_RAG_ADVANCED_FEATURES':
      return { ...state, showRagAdvancedFeatures: !state.showRagAdvancedFeatures };
    case 'TOGGLE_BASIC_ADVANCED_FEATURES':
      return { ...state, showBasicAdvancedFeatures: !state.showBasicAdvancedFeatures };
    case 'SET_WAIT_START_TIME': return { ...state, waitStartTime: action.payload };
    case 'SET_IS_LONG_WAIT': return { ...state, isLongWait: action.payload };
    case 'RESET_STREAMING':
      return {
        ...state,
        isStreaming: false,
        ragStreamContent: '',
        basicStreamContent: '',
        currentStage: '',
        progress: 0,
        progressStage: 'preparing',
        waitStartTime: null,
        isLongWait: false
      };
    case 'RESET_CHAT':
        return {
            ...state,
            question: '',
            followUpQuestion: '',
            response: null,
            selectedAnswer: null,
            showFeedbackPopup: false,
            error: '',
            ragStreamContent: '',
            basicStreamContent: '',
            isStreaming: false,
            currentStage: '',
        };
    case 'SUBMIT_QUESTION':
        return {
            ...state,
            loading: true,
            error: '',
            response: null,
            selectedAnswer: null,
            progress: 0,
            progressStage: 'preparing',
            ragStreamContent: '',
            basicStreamContent: '',
            isStreaming: state.useStreaming,
        }
    case 'SUBMIT_FOLLOW_UP':
        return {
            ...state,
            question: state.followUpQuestion,
            followUpQuestion: '',
            response: null,
            selectedAnswer: null,
            showFeedbackPopup: false,
            error: '',
        }
    default:
      return state;
  }
};


export const ChatComparison: React.FC = () => {
  const [state, dispatch] = useReducer(chatReducer, initialState);
  const {
      question, response, selectedAnswer, loading, error, showFeedbackPopup,
      userProfile, progress, progressStage, followUpQuestion, showManual,
      isListening, isSpeaking, speechSupported, isStreaming, ragStreamContent, basicStreamContent,
      useStreaming, currentStage
  } = state;

  // Accessibility: user-adjustable font size
  const [fontScale, setFontScale] = useState<number>(() => {
    const stored = localStorage.getItem('chat-font-scale');
    return stored ? parseFloat(stored) : 1;
  });

  const increaseFont = () => {
    const newScale = Math.min(fontScale + 0.1, 1.5);
    setFontScale(newScale);
    localStorage.setItem('chat-font-scale', newScale.toString());
  };

  const decreaseFont = () => {
    const newScale = Math.max(fontScale - 0.1, 0.8);
    setFontScale(newScale);
    localStorage.setItem('chat-font-scale', newScale.toString());
  };

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const speechSynthRef = useRef<SpeechSynthesisUtterance | null>(null);

  const toggleManual = (show: boolean) => {
    dispatch({ type: 'SET_SHOW_MANUAL', payload: show });
  };


  useEffect(() => {
    const loadConversationHistory = async () => {
      try {
        const history = await chatAPI.getConversationHistory();
        dispatch({ type: 'SET_CONVERSATION_HISTORY', payload: history });
      } catch (err) {
        console.error('Error loading conversation history:', err);
      }
    };
    
    const loadUserProfile = async () => {
      try {
        const profile = await chatAPI.getUserProfile();
        dispatch({ type: 'SET_USER_PROFILE', payload: profile });
      } catch (err) {
        console.error('Error loading user profile:', err);
      }
    };

    const initializeSpeechRecognition = () => {
        if ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window) {
            dispatch({ type: 'SET_SPEECH_SUPPORTED', payload: true });
            const SpeechRecognitionAPI = window.SpeechRecognition || window.webkitSpeechRecognition;
            recognitionRef.current = new SpeechRecognitionAPI();
            
            if (recognitionRef.current) {
            recognitionRef.current.continuous = false;
            recognitionRef.current.interimResults = false;
            recognitionRef.current.lang = 'pl-PL'; // Polish language
            
            recognitionRef.current.onstart = () => {
                dispatch({ type: 'SET_IS_LISTENING', payload: true });
                dispatch({ type: 'SET_ERROR', payload: '' });
            };
            
            recognitionRef.current.onend = () => {
                dispatch({ type: 'SET_IS_LISTENING', payload: false });
            };
            
            recognitionRef.current.onerror = (event) => {
                dispatch({ type: 'SET_IS_LISTENING', payload: false });
                if (event.error !== 'aborted') {
                    dispatch({ type: 'SET_ERROR', payload: 'Błąd rozpoznawania mowy. Spróbuj ponownie.' });
                }
            };
            
            recognitionRef.current.onresult = (event) => {
                const transcript = event.results[0][0].transcript;
                dispatch({ type: 'SET_QUESTION', payload: transcript });
                dispatch({ type: 'SET_IS_LISTENING', payload: false });
            };
            }
        } else {
            dispatch({ type: 'SET_SPEECH_SUPPORTED', payload: false });
        }
    };

    loadConversationHistory();
    loadUserProfile();
    initializeSpeechRecognition();
    
    return () => {
      if (recognitionRef.current && isListening) {
        recognitionRef.current.abort();
      }
      if ('speechSynthesis' in window && isSpeaking) {
        window.speechSynthesis.cancel();
      }
    };
  }, [isListening, isSpeaking]);

  // Slow mock progress for senior-friendly UX (60% → 95% over ~3 minutes)
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;

    if (loading && progress >= 60 && progress < 95) {
      // Increment progress slowly: 35% over 180 seconds = ~0.2% per second
      // Update every 2 seconds with ~0.4% increment for smoother UX
      timer = setInterval(() => {
        dispatch({ type: 'SET_PROGRESS', payload: Math.min(progress + 0.4, 95) });
      }, 2000);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [loading, progress]);

  const handleFollowUpSubmit = async () => {
    if (!followUpQuestion.trim()) return;
    dispatch({ type: 'SUBMIT_FOLLOW_UP' });
    await handleSubmit(undefined, followUpQuestion.trim());
  };

  const startListening = () => {
    if (recognitionRef.current && speechSupported && !isListening) {
      try {
        recognitionRef.current.start();
      } catch (error) {
        dispatch({ type: 'SET_ERROR', payload: 'Nie można uruchomić rozpoznawania mowy.' });
      }
    }
  };

  const stopListening = () => {
    if (recognitionRef.current && isListening) {
      recognitionRef.current.abort();
      dispatch({ type: 'SET_IS_LISTENING', payload: false });
    }
  };

  const speakText = (text: string) => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      
      const cleanText = text
        .replace(/[#*_`]/g, '')
        .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
        .replace(/\n+/g, '. ');
      
      const utterance = new SpeechSynthesisUtterance(cleanText);
      utterance.lang = 'pl-PL';
      utterance.rate = 0.9;
      utterance.pitch = 1;
      utterance.volume = 1;
      
      utterance.onstart = () => dispatch({ type: 'SET_IS_SPEAKING', payload: true });
      utterance.onend = () => {
          dispatch({ type: 'SET_IS_SPEAKING', payload: false });
          speechSynthRef.current = null;
      };
      utterance.onerror = () => {
        dispatch({ type: 'SET_IS_SPEAKING', payload: false });
        speechSynthRef.current = null;
        dispatch({ type: 'SET_ERROR', payload: 'Błąd podczas odczytu tekstu.' });
      };
      
      speechSynthRef.current = utterance;
      window.speechSynthesis.speak(utterance);
    } else {
        dispatch({ type: 'SET_ERROR', payload: 'Przeglądarka nie obsługuje syntezy mowy.' });
    }
  };

  const stopSpeaking = () => {
    if ('speechSynthesis' in window && isSpeaking) {
      window.speechSynthesis.cancel();
      dispatch({ type: 'SET_IS_SPEAKING', payload: false });
      speechSynthRef.current = null;
    }
  };

  const handleSubmit = async (e?: React.FormEvent, customQuestion?: string) => {
    if (e) e.preventDefault();
    const questionToSubmit = customQuestion || question;
    if (!questionToSubmit.trim()) return;

    dispatch({ type: 'SUBMIT_QUESTION' });

    // Use streaming if enabled
    if (useStreaming) {
      try {
        dispatch({ type: 'SET_IS_STREAMING', payload: true });
        dispatch({ type: 'SET_RAG_STREAM_CONTENT', payload: '' });
        dispatch({ type: 'SET_BASIC_STREAM_CONTENT', payload: '' });

        const result = await chatAPI.streamingChat(
          questionToSubmit,
          userProfile?.clinical_info || "Brak dostępnych informacji klinicznych.",
          (progress) => dispatch({ type: 'SET_PROGRESS', payload: progress }),
          (chunk) => dispatch({ type: 'APPEND_RAG_CHUNK', payload: chunk }),
          (chunk) => dispatch({ type: 'APPEND_BASIC_CHUNK', payload: chunk }),
          (stage, progress) => {
            dispatch({ type: 'SET_CURRENT_STAGE', payload: stage });
            if (progress !== undefined) {
              dispatch({ type: 'SET_PROGRESS', payload: progress });
            }
          }
        );

        dispatch({ type: 'SET_RESPONSE', payload: result as ChatResponse });
        dispatch({ type: 'SET_QUESTION', payload: '' });
        
      } catch (err) {
        dispatch({ type: 'SET_ERROR', payload: 'Wystąpił błąd podczas generowania odpowiedzi. Spróbuj ponownie.' });
      } finally {
        setTimeout(() => {
          dispatch({ type: 'SET_LOADING', payload: false });
          dispatch({ type: 'SET_IS_STREAMING', payload: false });
          dispatch({ type: 'SET_PROGRESS', payload: 0 });
          dispatch({ type: 'SET_PROGRESS_STAGE', payload: 'preparing'});
        }, 800);
      }
    } else {
      // Fallback to original non-streaming implementation
      const progressSimulation = () => {
        return new Promise<void>((resolve) => {
          const stages = [
            { stage: 'preparing' as const, progress: 15, duration: 400 },
            { stage: 'sending' as const, progress: 35, duration: 600 },
            { stage: 'analyzing' as const, progress: 60, duration: 800 },
            { stage: 'generating' as const, progress: 85, duration: 1000 },
            { stage: 'finalizing' as const, progress: 95, duration: 300 }
          ];
          let currentStageIndex = 0;
          
          const runNextStage = () => {
            if (currentStageIndex < stages.length) {
              const currentStage = stages[currentStageIndex];
              dispatch({ type: 'SET_PROGRESS_STAGE', payload: currentStage.stage });
              
              const startProgress = currentStageIndex === 0 ? 0 : stages[currentStageIndex - 1].progress;
              const targetProgress = currentStage.progress;
              const progressDiff = targetProgress - startProgress;
              const steps = 20;
              const stepSize = progressDiff / steps;
              const stepDuration = currentStage.duration / steps;
              
              let step = 0;
              const progressInterval = setInterval(() => {
                step++;
                const newProgress = startProgress + (stepSize * step);
                dispatch({ type: 'SET_PROGRESS', payload: Math.min(newProgress, targetProgress) });
                
                if (step >= steps) {
                  clearInterval(progressInterval);
                  currentStageIndex++;
                  setTimeout(runNextStage, 100);
                }
              }, stepDuration);
            } else {
              resolve();
            }
          };
          runNextStage();
        });
      };

      try {
        const progressPromise = progressSimulation();
        
        const result = await chatAPI.askQuestion(questionToSubmit, () => {}) as ChatResponse;
        
        await progressPromise;

        dispatch({ type: 'SET_PROGRESS_STAGE', payload: 'complete' });
        dispatch({ type: 'SET_PROGRESS', payload: 100 });
        
        setTimeout(() => {
          dispatch({ type: 'SET_RESPONSE', payload: result });
          dispatch({ type: 'SET_QUESTION', payload: '' });
        }, 300);
      } catch (err) {
          dispatch({ type: 'SET_ERROR', payload: 'Wystąpił błąd podczas generowania odpowiedzi. Spróbuj ponownie.' });
      } finally {
        setTimeout(() => {
          dispatch({ type: 'SET_LOADING', payload: false });
          dispatch({ type: 'SET_PROGRESS', payload: 0 });
          dispatch({ type: 'SET_PROGRESS_STAGE', payload: 'preparing'});
        }, 800);
      }
    }
  };

  const handleSelectAnswer = async (answerType: 'rag' | 'basic') => {
    if (!response || selectedAnswer) return;

    try {
      await chatAPI.selectAnswer(response.conversation_id, answerType);
      dispatch({ type: 'SET_SELECTED_ANSWER', payload: answerType });
    } catch (err) {
        dispatch({ type: 'SET_ERROR', payload: 'Wystąpił błąd podczas zapisywania wyboru.' });
    }
  };

  const handleSubmitFeedback = async (feedbackText: string) => {
    if (!response) return;

    try {
      await chatAPI.submitFeedback(response.conversation_id, feedbackText);
      dispatch({ type: 'SET_SHOW_FEEDBACK_POPUP', payload: false });
      const history = await chatAPI.getConversationHistory();
      dispatch({ type: 'SET_CONVERSATION_HISTORY', payload: history });
    } catch (err) {
        dispatch({ type: 'SET_ERROR', payload: 'Wystąpił błąd podczas zapisywania opinii.' });
        throw err; // Re-throw to let modal handle the error
    }
  };

  const handleSkipFeedback = () => {
    dispatch({ type: 'SET_SHOW_FEEDBACK_POPUP', payload: false });
  };

  const startNewQuestion = () => {
    dispatch({ type: 'RESET_CHAT' });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const getProgressStatusText = (stage: string) => {
    switch (stage) {
      case 'preparing': return 'Przygotowywanie zapytania...';
      case 'sending': return 'Wysyłanie do asystenta AI...';
      case 'analyzing': return 'Analizowanie treści pytania...';
      case 'generating': return 'Generowanie odpowiedzi...';
      case 'finalizing': return 'Finalizowanie odpowiedzi...';
      case 'complete': return 'Gotowe!';
      default: return 'Przetwarzanie...';
    }
  };

  const ragAnswer = response?.answers.find(a => a.type === 'rag');
  const basicAnswer = response?.answers.find(a => a.type === 'basic');

  return (
    <div className="chat-container" style={{ fontSize: `${fontScale}rem` }}>
      {error && <div className="error-message" role="alert">{error}</div>}

      {!showManual && (
        <div className="quick-help-toggle" style={{marginBottom:'1rem'}}>
          <button 
            className="help-toggle-button"
            onClick={() => toggleManual(true)}
            tabIndex={0}
            aria-label="Pokaż instrukcję obsługi systemu Radonek"
            title="Kliknij aby zobaczyć szczegółową instrukcję obsługi"
          >
            <FontAwesomeIcon icon={faBook} />
            <span>Potrzebujesz pomocy? Kliknij tutaj aby zobaczyć instrukcję obsługi</span>
            <FontAwesomeIcon icon={faChevronDown} />
          </button>
        </div>
      )}

      {showManual && (
        <div className="user-manual card" style={{marginBottom:'2rem'}}>
          <div 
            className="manual-header" 
            onClick={() => toggleManual(false)} 
            style={{cursor: 'pointer'}}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleManual(false);
              }
            }}
            role="button"
            aria-label="Ukryj instrukcję obsługi"
            title="Kliknij aby ukryć instrukcję obsługi"
          >
            <h2>
              <FontAwesomeIcon icon={faBook} /> Instrukcja obsługi systemu Radonek
              <FontAwesomeIcon 
                icon={faChevronUp} 
                style={{fontSize: '1rem', marginLeft: 'auto'}}
              />
            </h2>
            <p className="manual-subtitle">
              Kliknij aby ukryć instrukcję • Naciśnij klawisz Tab aby nawigować
            </p>
          </div>
          <div className="manual-content">
            <div className="manual-section">
              <h3><FontAwesomeIcon icon={faComment} /> 1. Jak zadać pytanie?</h3>
              <div className="manual-steps">
                <p><strong>Krok 1:</strong> W polu poniżej wpisz swoje pytanie zwykłymi słowami</p>
                <p><strong>Przykłady pytań:</strong></p>
                <ul>
                  <li>"Jak przygotować się do radioterapii?"</li>
                  <li>"Jakie są skutki uboczne mojego leczenia?"</li>
                  <li>"Kiedy będzie następna wizyta?"</li>
                  <li>"Co powinienem jeść podczas leczenia?"</li>
                </ul>
                <p><strong>Krok 2:</strong> Naciśnij przycisk <strong>"Wyślij pytanie"</strong> lub klawisz <strong>Enter</strong></p>
                {speechSupported && (
                  <p><strong>Alternatywa:</strong> Naciśnij przycisk <strong>"Nagraj"</strong> 🎤 i powiedz pytanie głosem</p>
                )}
              </div>
            </div>

            <div className="manual-section">
              <h3><FontAwesomeIcon icon={faDatabase} /> 2. Jak działają odpowiedzi?</h3>
              <div className="manual-steps">
                <p>System daje Państwu <strong>dwie odpowiedzi do porównania:</strong></p>
                <div className="answer-types">
                  <div className="answer-type">
                    <h4><FontAwesomeIcon icon={faDatabase} /> Odpowiedź "Z bazą wiedzy"</h4>
                    <p>Wykorzystuje medyczną bazę wiedzy i wytyczne kliniczne. Zazwyczaj bardziej szczegółowa.</p>
                  </div>
                  <div className="answer-type">
                    <h4><FontAwesomeIcon icon={faRobot} /> Odpowiedź "Podstawowa"</h4>
                    <p>Oparta na Pana/Pani indywidualnych danych medycznych. Zazwyczaj bardziej personalna.</p>
                  </div>
                </div>
                <p><strong>Ważne:</strong> Przeczytaj obie odpowiedzi i wybierz tę, która wydaje się bardziej pomocna</p>
              </div>
            </div>

            <div className="manual-section">
              <h3><FontAwesomeIcon icon={faVolumeUp} /> 3. Funkcja czytania na głos</h3>
              <div className="manual-steps">
                <p>Jeśli ma Pan/Pani problemy ze wzrokiem lub wolą słuchać:</p>
                <p><strong>• Naciśnij przycisk głośnika</strong> 🔊 przy odpowiedzi</p>
                <p><strong>• System przeczyta odpowiedź na głos</strong></p>
                <p><strong>• Można zatrzymać czytanie</strong> naciskając przycisk ⏹️</p>
                <p><strong>Wskazówka:</strong> Używaj słuchawek w głośnym otoczeniu</p>
              </div>
            </div>

            <div className="manual-section">
              <h3><FontAwesomeIcon icon={faCheck} /> 4. Co po wybraniu odpowiedzi?</h3>
              <div className="manual-steps">
                <p><strong>Po wybraniu lepszej odpowiedzi może Pan/Pani:</strong></p>
                <ul>
                  <li><strong>Zadać pytanie uzupełniające</strong> - jeśli potrzebuje więcej informacji</li>
                  <li><strong>Dodać opinię</strong> - podzielić się tym, czy odpowiedź była pomocna</li>
                  <li><strong>Zadać nowe pytanie</strong> - rozpocząć od nowa</li>
                </ul>
              </div>
            </div>

            <div className="manual-section">
              <h3>⚠️ Ważne przypomnienia</h3>
              <div className="manual-important">
                <p><strong>🚨 W nagłych przypadkach:</strong> Nie używaj tego systemu! Zadzwoń pod 112 lub idź do szpitala</p>
                <p><strong>💊 Leki i dawki:</strong> Zawsze pytaj lekarza o zmiany w lekach</p>
                <p><strong>🔒 Prywatność:</strong> Twoje dane są bezpieczne i chronione</p>
                <p><strong>❓ Problemy techniczne:</strong> Skontaktuj się z personelem medycznym</p>
              </div>
            </div>

            <div className="manual-section">
              <h3><FontAwesomeIcon icon={faMicrophone} /> 5. Skróty klawiszowe i wskazówki</h3>
              <div className="manual-shortcuts">
                <p><strong>⌨️ Enter</strong> - wyślij pytanie</p>
                <p><strong>⌨️ Shift + Enter</strong> - nowa linia w tekście</p>
                <p><strong>🎤 Przycisk "Nagraj"</strong> - nagraj pytanie głosem</p>
                <p><strong>🔊 Przycisk głośnika</strong> - posłuchaj odpowiedzi</p>
                <p><strong>💡 Wskazówka:</strong> Używaj prostych, konkretnych pytań</p>
              </div>
            </div>

            <div className="manual-section">
              <h3>🔧 Rozwiązywanie problemów</h3>
              <div className="manual-troubleshooting">
                <div className="troubleshooting-item">
                  <h4>❌ Nie mogę zadać pytania</h4>
                  <ul>
                    <li>Sprawdź czy pole z pytaniem nie jest puste</li>
                    <li>Spróbuj odświeżyć stronę (F5)</li>
                    <li>Sprawdź połączenie z internetem</li>
                  </ul>
                </div>
                <div className="troubleshooting-item">
                  <h4>🎤 Mikrofon nie działa</h4>
                  <ul>
                    <li>Sprawdź czy zezwoliłeś przeglądarce na dostęp do mikrofonu</li>
                    <li>Sprawdź czy mikrofon jest podłączony i włączony</li>
                    <li>Spróbuj wpisać pytanie ręcznie</li>
                  </ul>
                </div>
                <div className="troubleshooting-item">
                  <h4>🔊 Nie słychać czytania na głos</h4>
                  <ul>
                    <li>Sprawdź czy głośniki/słuchawki są podłączone</li>
                    <li>Zwiększ głośność w komputerze</li>
                    <li>Sprawdź czy przeglądarka ma włączony dźwięk</li>
                  </ul>
                </div>
                <div className="troubleshooting-item">
                  <h4>💭 Nie rozumiem odpowiedzi</h4>
                  <ul>
                    <li>Zadaj pytanie uzupełniające o wyjaśnienie</li>
                    <li>Poproś o prostsze wytłumaczenie</li>
                    <li>Skontaktuj się z personelem medycznym</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <form className="question-form card" onSubmit={handleSubmit}>
        <div className="form-header">
          <div className="form-header-content">
            <div className="form-title">
              <h3><FontAwesomeIcon icon={faComment} /> Zadaj pytanie asystentowi AI</h3>
              <p className="form-subtitle">Wpisz swoje pytanie w polu poniżej i naciśnij "Wyślij"</p>
            </div>
            <div className="form-controls">
              <button
                type="button"
                className="text-size-button"
                onClick={increaseFont}
                aria-label="Zwiększ rozmiar tekstu"
                tabIndex={0}
              >
                A+
              </button>
              <button
                type="button"
                className="text-size-button"
                onClick={decreaseFont}
                aria-label="Zmniejsz rozmiar tekstu"
                tabIndex={0}
              >
                A-
              </button>
            </div>
          </div>
        </div>
        <div className="form-content">
          <div className="question-input-group">
            <textarea
              className="question-input accessible-textarea"
              value={question}
              onChange={e => dispatch({ type: 'SET_QUESTION', payload: e.target.value })}
              onKeyDown={handleKeyDown}
              placeholder="Na przykład: Jak będzie wyglądać leczenie? Kiedy będą wizyty kontrolne? Jak przygotować się do radioterapii?"
              required
              rows={3}
              tabIndex={0}
              aria-label="Pole do wpisania pytania"
              disabled={loading}
            />
            <div className="input-buttons">
              <button
                type="button"
                className={`voice-button ${isListening ? 'listening' : ''}`}
                onClick={isListening ? stopListening : startListening}
                disabled={loading || !speechSupported}
                title={
                  !speechSupported
                    ? 'Rozpoznawanie mowy nie jest dostępne w tej przeglądarce'
                    : isListening
                      ? 'Zatrzymaj nagrywanie'
                      : 'Nagraj pytanie głosem'
                }
                aria-label={
                  !speechSupported
                    ? 'Rozpoznawanie mowy nie jest dostępne w tej przeglądarce'
                    : isListening
                      ? 'Zatrzymaj nagrywanie'
                      : 'Nagraj pytanie głosem'
                }
                tabIndex={0}
              >
                <FontAwesomeIcon
                  icon={isListening ? faMicrophoneSlash : faMicrophone}
                  className="button-icon"
                />
                <span className="button-text">
                  {isListening ? 'Nagrywanie...' : 'Nagraj'}
                </span>
              </button>
              <button type="submit" className="ask-button" disabled={loading || !question.trim()} tabIndex={0}>
                <FontAwesomeIcon icon={faPaperPlane} className="button-icon" />
                <span className="button-text">{loading ? 'Wysyłanie...' : 'Wyślij pytanie'}</span>
              </button>
            </div>
          </div>
          <div className="form-hint">
            <p><FontAwesomeIcon icon={faQuestion} /> Wskazówka: Zadawaj konkretne pytania dotyczące swojego zdrowia lub leczenia</p>
            <p>⌨️ Naciśnij Enter aby wysłać pytanie (Shift+Enter dla nowej linii)</p>
            {speechSupported && (
              <p>🎤 Użyj przycisku "Nagraj" aby podyktować pytanie głosem</p>
            )}
            <div className="streaming-toggle-container">
              <label className="streaming-toggle">
                <input
                  type="checkbox"
                  checked={useStreaming}
                  onChange={(e) => {
                    dispatch({ type: 'SET_USE_STREAMING', payload: e.target.checked });
                  }}
                />
                <span className="toggle-slider"></span>
                <span className="toggle-label">Odpowiedź na żywo</span>
              </label>
            </div>
          </div>
        </div>
      </form>

      {loading && (
        <div className="answers-container card" style={{marginTop:'2rem'}}>
          <div className="answers-header">
            <h3>Generowanie odpowiedzi...</h3>
            <div className="progress-container">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <div className="progress-info">
                <span className="progress-text">{Math.round(progress)}%</span>
                <span className="progress-status">
                  {currentStage || getProgressStatusText(progressStage)}
                </span>
              </div>

              {/* Reassurance text for seniors (shown during generation phase) */}
              {progress >= 60 && (
                <p className="progress-reassurance">
                  Generowanie odpowiedzi może potrwać 2-3 minuty. Proszę cierpliwie czekać.
                </p>
              )}
            </div>
          </div>
          {isStreaming ? (
            <div className="answers-comparison">
              <div className="answer-column card streaming">
                <div className="answer-header">
                  <h3><FontAwesomeIcon icon={faDatabase} /> Z bazą wiedzy</h3>
                  <div className="answer-controls">
                    <span className="answer-badge rag-badge">RAG</span>
                    <div className="streaming-indicator">
                      <FontAwesomeIcon icon={faPaperPlane} className="typing-icon" />
                      <span>Generowanie...</span>
                    </div>
                  </div>
                </div>
                <div className="answer-content streaming-content" aria-live="polite">
                  <ReactMarkdown 
                    components={{
                      p: ({children}) => <p>{children}</p>,
                      br: () => <br />,
                    }}
                  >
                    {ragStreamContent || "Rozpoczynam generowanie odpowiedzi..."}
                  </ReactMarkdown>
                  {ragStreamContent && <span className="streaming-cursor">|</span>}
                </div>
              </div>
              <div className="answer-column card streaming">
                <div className="answer-header">
                  <h3><FontAwesomeIcon icon={faRobot} /> Podstawowa</h3>
                  <div className="answer-controls">
                    <span className="answer-badge basic-badge">Podstawowa</span>
                    <div className="streaming-indicator">
                      <FontAwesomeIcon icon={faPaperPlane} className="typing-icon" />
                      <span>Generowanie...</span>
                    </div>
                  </div>
                </div>
                <div className="answer-content streaming-content" aria-live="polite">
                  <ReactMarkdown 
                    components={{
                      p: ({children}) => <p>{children}</p>,
                      br: () => <br />,
                    }}
                  >
                    {basicStreamContent || "Rozpoczynam generowanie odpowiedzi..."}
                  </ReactMarkdown>
                  {basicStreamContent && <span className="streaming-cursor">|</span>}
                </div>
              </div>
            </div>
          ) : (
            <div className="answers-comparison">
              <div className="answer-column card"><Skeleton height="4em" /></div>
              <div className="answer-column card"><Skeleton height="4em" /></div>
            </div>
          )}
        </div>
      )}

      {response && !selectedAnswer && (
        <div className="answers-container card" style={{marginTop:'2rem'}}>
          <div className="answers-header">
            <h3>Porównanie odpowiedzi</h3>
            <p className="answers-subtitle">Wybierz odpowiedź, która wydaje Ci się bardziej pomocna:</p>
          </div>
          <div className="answers-comparison">
            <div className={`answer-column card`}
                 tabIndex={0} aria-label="Odpowiedź z bazą wiedzy">
              <div className="answer-header">
                <h3><FontAwesomeIcon icon={faDatabase} /> Z bazą wiedzy</h3>
                <div className="answer-controls">
                  <span className="answer-badge rag-badge">RAG</span>
                  <button
                    type="button"
                    className="speak-button"
                    onClick={() => speakText(ragAnswer?.content || '')}
                    disabled={isSpeaking || !ragAnswer?.content}
                    title="Przeczytaj odpowiedź na głos"
                    tabIndex={0}
                  >
                    <FontAwesomeIcon icon={faVolumeUp} />
                  </button>
                </div>
              </div>
              <div className="answer-content" aria-live="polite">
                <ReactMarkdown 
                  components={{
                    p: ({children}) => <p>{children}</p>,
                    br: () => <br />,
                  }}
                >
                  {ragAnswer?.content || ''}
                </ReactMarkdown>
              </div>
              
              {/* GPT-OSS Advanced Features for RAG */}
              {(ragAnswer?.thinking_process || ragAnswer?.web_search_queries || ragAnswer?.tool_calls) && (
                <div className="advanced-features">
                  <div className="features-header">
                    <h4>🔬 Zaawansowane funkcje</h4>
                  </div>
                  
                  {ragAnswer?.thinking_process && (
                    <div className="feature-section">
                      <button 
                        className={`feature-toggle ${state.showRagAdvancedFeatures ? 'expanded' : ''}`}
                        onClick={() => dispatch({ type: 'TOGGLE_RAG_ADVANCED_FEATURES' })}
                      >
                        <FontAwesomeIcon icon={faBrain} />
                        🧠 Proces myślenia
                        <FontAwesomeIcon icon={state.showRagAdvancedFeatures ? faChevronUp : faChevronDown} />
                      </button>
                      {state.showRagAdvancedFeatures && (
                        <div className="feature-content thinking-content">
                          <pre>{ragAnswer.thinking_process}</pre>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {ragAnswer?.web_search_queries && ragAnswer.web_search_queries.length > 0 && (
                    <div className="feature-section">
                      <button 
                        className={`feature-toggle ${state.showRagAdvancedFeatures ? 'expanded' : ''}`}
                        onClick={() => dispatch({ type: 'TOGGLE_RAG_ADVANCED_FEATURES' })}
                      >
                        <FontAwesomeIcon icon={faGlobe} />
                        🌐 Wyszukiwanie internetowe
                        <FontAwesomeIcon icon={state.showRagAdvancedFeatures ? faChevronUp : faChevronDown} />
                      </button>
                      {state.showRagAdvancedFeatures && (
                        <div className="feature-content web-search-content">
                          <div className="search-queries">
                            <h5>Zapytania:</h5>
                            <ul>
                              {ragAnswer.web_search_queries.map((query, index) => (
                                <li key={index}>{query}</li>
                              ))}
                            </ul>
                          </div>
                          {ragAnswer?.web_search_results && ragAnswer.web_search_results.length > 0 && (
                            <div className="search-results">
                              <h5>Wyniki:</h5>
                              <ul>
                                {ragAnswer.web_search_results.map((result, index) => (
                                  <li key={index}>{result}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {ragAnswer?.tool_calls && ragAnswer.tool_calls.length > 0 && (
                    <div className="feature-section">
                      <button 
                        className={`feature-toggle ${state.showRagAdvancedFeatures ? 'expanded' : ''}`}
                        onClick={() => dispatch({ type: 'TOGGLE_RAG_ADVANCED_FEATURES' })}
                      >
                        <FontAwesomeIcon icon={faTools} />
                        🔧 Wywołania narzędzi
                        <FontAwesomeIcon icon={state.showRagAdvancedFeatures ? faChevronUp : faChevronDown} />
                      </button>
                      {state.showRagAdvancedFeatures && (
                        <div className="feature-content tool-calls-content">
                          <ul>
                            {ragAnswer.tool_calls.map((call, index) => (
                              <li key={index}>{call}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {ragAnswer?.confidence_level && (
                    <div className="confidence-indicator">
                      <span className="confidence-label">📊 Poziom pewności:</span>
                      <span className="confidence-value">{ragAnswer.confidence_level}</span>
                    </div>
                  )}
                </div>
              )}
              
              <button className="select-button"
                      onClick={() => handleSelectAnswer('rag')} tabIndex={0}>
                <FontAwesomeIcon icon={faCheck} className="button-icon" />
                Wybierz tę odpowiedź
              </button>
            </div>
            <div className={`answer-column card`}
                 tabIndex={0} aria-label="Odpowiedź podstawowa">
              <div className="answer-header">
                <h3><FontAwesomeIcon icon={faRobot} /> Podstawowa</h3>
                <div className="answer-controls">
                  <span className="answer-badge basic-badge">Podstawowa</span>
                  <button
                    type="button"
                    className="speak-button"
                    onClick={() => speakText(basicAnswer?.content || '')}
                    disabled={isSpeaking || !basicAnswer?.content}
                    title="Przeczytaj odpowiedź na głos"
                    tabIndex={0}
                  >
                    <FontAwesomeIcon icon={faVolumeUp} />
                  </button>
                </div>
              </div>
              <div className="answer-content" aria-live="polite">
                <ReactMarkdown 
                  components={{
                    p: ({children}) => <p>{children}</p>,
                    br: () => <br />,
                  }}
                >
                  {basicAnswer?.content || ''}
                </ReactMarkdown>
              </div>
              
              {/* GPT-OSS Advanced Features for Basic */}
              {(basicAnswer?.thinking_process || basicAnswer?.web_search_queries || basicAnswer?.tool_calls) && (
                <div className="advanced-features">
                  <div className="features-header">
                    <h4>🔬 Zaawansowane funkcje</h4>
                  </div>
                  
                  {basicAnswer?.thinking_process && (
                    <div className="feature-section">
                      <button 
                        className={`feature-toggle ${state.showBasicAdvancedFeatures ? 'expanded' : ''}`}
                        onClick={() => dispatch({ type: 'TOGGLE_BASIC_ADVANCED_FEATURES' })}
                      >
                        <FontAwesomeIcon icon={faBrain} />
                        🧠 Proces myślenia
                        <FontAwesomeIcon icon={state.showBasicAdvancedFeatures ? faChevronUp : faChevronDown} />
                      </button>
                      {state.showBasicAdvancedFeatures && (
                        <div className="feature-content thinking-content">
                          <pre>{basicAnswer.thinking_process}</pre>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {basicAnswer?.web_search_queries && basicAnswer.web_search_queries.length > 0 && (
                    <div className="feature-section">
                      <button 
                        className={`feature-toggle ${state.showBasicAdvancedFeatures ? 'expanded' : ''}`}
                        onClick={() => dispatch({ type: 'TOGGLE_BASIC_ADVANCED_FEATURES' })}
                      >
                        <FontAwesomeIcon icon={faGlobe} />
                        🌐 Wyszukiwanie internetowe
                        <FontAwesomeIcon icon={state.showBasicAdvancedFeatures ? faChevronUp : faChevronDown} />
                      </button>
                      {state.showBasicAdvancedFeatures && (
                        <div className="feature-content web-search-content">
                          <div className="search-queries">
                            <h5>Zapytania:</h5>
                            <ul>
                              {basicAnswer.web_search_queries.map((query, index) => (
                                <li key={index}>{query}</li>
                              ))}
                            </ul>
                          </div>
                          {basicAnswer?.web_search_results && basicAnswer.web_search_results.length > 0 && (
                            <div className="search-results">
                              <h5>Wyniki:</h5>
                              <ul>
                                {basicAnswer.web_search_results.map((result, index) => (
                                  <li key={index}>{result}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {basicAnswer?.tool_calls && basicAnswer.tool_calls.length > 0 && (
                    <div className="feature-section">
                      <button 
                        className={`feature-toggle ${state.showBasicAdvancedFeatures ? 'expanded' : ''}`}
                        onClick={() => dispatch({ type: 'TOGGLE_BASIC_ADVANCED_FEATURES' })}
                      >
                        <FontAwesomeIcon icon={faTools} />
                        🔧 Wywołania narzędzi
                        <FontAwesomeIcon icon={state.showBasicAdvancedFeatures ? faChevronUp : faChevronDown} />
                      </button>
                      {state.showBasicAdvancedFeatures && (
                        <div className="feature-content tool-calls-content">
                          <ul>
                            {basicAnswer.tool_calls.map((call, index) => (
                              <li key={index}>{call}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {basicAnswer?.confidence_level && (
                    <div className="confidence-indicator">
                      <span className="confidence-label">📊 Poziom pewności:</span>
                      <span className="confidence-value">{basicAnswer.confidence_level}</span>
                    </div>
                  )}
                </div>
              )}
              
              <button className="select-button"
                      onClick={() => handleSelectAnswer('basic')} tabIndex={0}>
                <FontAwesomeIcon icon={faCheck} className="button-icon" />
                Wybierz tę odpowiedź
              </button>
            </div>
          </div>
        </div>
      )}

      {response && selectedAnswer && (
        <div className="selected-answer-container card expanded-answer" style={{marginTop:'2rem'}}>
          <div className="selected-answer-header">
            <h3><FontAwesomeIcon icon={faCheck} /> Wybrana odpowiedź</h3>
            <div className="answer-controls">
              <span className={`answer-badge ${selectedAnswer === 'rag' ? 'rag-badge' : 'basic-badge'}`}>
                <FontAwesomeIcon icon={selectedAnswer === 'rag' ? faDatabase : faRobot} />
                {selectedAnswer === 'rag' ? ' Z bazą wiedzy' : ' Podstawowa'}
              </span>
              <button
                type="button"
                className={`speak-button ${isSpeaking ? 'speaking' : ''}`}
                onClick={() => isSpeaking ? stopSpeaking() : speakText(selectedAnswer === 'rag' ? ragAnswer?.content || '' : basicAnswer?.content || '')}
                disabled={!(selectedAnswer === 'rag' ? ragAnswer?.content : basicAnswer?.content)}
                title={isSpeaking ? 'Zatrzymaj czytanie' : 'Przeczytaj odpowiedź na głos'}
                tabIndex={0}
              >
                <FontAwesomeIcon icon={isSpeaking ? faStop : faVolumeUp} />
              </button>
            </div>
          </div>
          <div className="selected-answer-content" aria-live="polite">
            <ReactMarkdown 
              components={{
                p: ({children}) => <p>{children}</p>,
                br: () => <br />,
              }}
            >
              {selectedAnswer === 'rag' ? ragAnswer?.content || '' : basicAnswer?.content || ''}
            </ReactMarkdown>
          </div>
          
          {/* Follow-up question form */}
          <div className="follow-up-section">
            <h4><FontAwesomeIcon icon={faComment} /> Zadaj pytanie uzupełniające</h4>
            <div className="follow-up-input-group">
              <textarea
                className="follow-up-input"
                value={followUpQuestion}
                onChange={e => dispatch({ type: 'SET_FOLLOW_UP_QUESTION', payload: e.target.value })}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleFollowUpSubmit();
                  }
                }}
                placeholder="Masz dodatkowe pytanie? Zadaj je tutaj..."
                rows={2}
                tabIndex={0}
                aria-label="Pytanie uzupełniające"
                disabled={loading}
              />
              <button 
                className="follow-up-button"
                disabled={loading || !followUpQuestion.trim()}
                onClick={handleFollowUpSubmit}
                tabIndex={0}
              >
                <FontAwesomeIcon icon={faPaperPlane} className="button-icon" />
                Zadaj pytanie uzupełniające
              </button>
            </div>
          </div>

          <div className="answer-actions">
            <button className="secondary-button" onClick={startNewQuestion}>
              <FontAwesomeIcon icon={faRefresh} className="button-icon" />
              Zadaj nowe pytanie
            </button>
            {!showFeedbackPopup && (
              <button className="primary-button" onClick={() => dispatch({ type: 'SET_SHOW_FEEDBACK_POPUP', payload: true })}>
                <FontAwesomeIcon icon={faThumbsUp} className="button-icon" />
                Dodaj opinię
              </button>
            )}
          </div>
        </div>
      )}

      {/* Opinion Modal */}
      <OpinionModal
        isOpen={showFeedbackPopup}
        onClose={handleSkipFeedback}
        onSubmit={handleSubmitFeedback}
        title="Podziel się opinią"
        description="Czy odpowiedź była pomocna? Twoja opinia pomoże nam ulepszać system."
        placeholder="Napisz swoją opinię o otrzymanej odpowiedzi..."
        submitButtonText="Wyślij opinię"
        skipButtonText="Pomiń"
      />

      {/* Legacy feedback section removed - using OpinionModal instead */}
    </div>
  );
};
